import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { db, functionsAsia } from '~~/firebaseConfig'

// searchCreators onCall(functions/creator/creatorSearch.js) 응답 항목. 2026-09-18,
// FP #19 "크리에이터 검색 조회" 대응 - 민감정보(email/phone/정산계좌) 제외된
// 화이트리스트 필드만 담긴다.
export interface CreatorSearchResult {
  creatorId: string
  name: string
  profileImageUrl: string | null
  platform: string | null
  country: string | null
  grade: string | null
  keywords: string[]
  subscriberCount: number | null
  vibeScore: number | null
}

export interface CreatorSearchFilters {
  category?: string
  country?: string
  grade?: string
  minSubscribers?: number
  maxSubscribers?: number
  minVibeScore?: number
}

interface Creator {
  id: string
  name: string
  nickname: string
  email: string
  phone: string
  type: string
  // sign 내부 필드들
  addressRegion: string
  addressCity: string
  addressCountry: string
  addressDetail: string
  countryCode: string
  profileImageUrl?: string
  // snsChannels 내부 정보
  categories: string[]
  channel: string[]
  followers?: number
  isOwner?: boolean
  url?: string
  // 대표 채널(snsChannels[0])의 소유권 검증 상태. 관리자 승인 전까지는 항상
  // 'unverified' — admin/channel-verification 화면에서 승인/거절 처리.
  verificationStatus?: 'unverified' | 'verified' | 'rejected'
  // 추가 필드들
  ratingAverage?: number
  isActive?: boolean
  // 크리에이터 등급(2026-09-18, functions/youtube/creatorGrade.js가 구독자 수
  // 기반으로 자동 계산). YouTube 채널 미연동/배치 전까지는 null.
  grade?: string | null
  bio?: string
  introduction?: string
  joinDate?: string
  createdAt?: string
  updatedAt?: string
  keywords?: string[] // AI 분석으로 생성된 키워드 배열
}

export const useCreatorStore = defineStore('creator', () => {
  const systemStore = useSystemStore()
  const creators = ref<Creator[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // 모든 크리에이터 목록 가져오기
  const getCreators = async () => {
    loading.value = true
    error.value = null

    try {
      const snapshot = await getDocs(collection(db, 'creator'))

      if (snapshot.docs.length === 0) {
        creators.value = []
        return
      }

      creators.value = snapshot.docs.map((doc) => {
        const data = doc.data()

        // sign 객체에서 주소 및 프로필 정보 추출
        const signData = data.sign || {}

        // sign.snsChannels에서 categories와 channel 정보 추출
        const snsChannelsData = signData.snsChannels || {}
        let categories: string[] = []
        const channels: string[] = []
        let followers = 0
        let url = ''

        // 대표 채널(snsChannels[0])의 검증 상태. snsChannels가 배열이 아니라
        // {0: {...}, 1: {...}} 형태로 저장된 레거시 문서도 있어(Object.keys 순회
        // 방식과 동일하게) Array.isArray로 먼저 분기한다.
        const snsChannelsList = Array.isArray(signData.snsChannels) ? signData.snsChannels : Object.values(snsChannelsData)
        const verificationStatus = snsChannelsList[0]?.verificationStatus || 'unverified'

        // snsChannels 내부의 각 계정 정보 처리 (0, 1 등의 키)
        Object.keys(snsChannelsData).forEach((key) => {
          const channelData = snsChannelsData[key]
          if (channelData.categories && Array.isArray(channelData.categories)) {
            categories = [...categories, ...channelData.categories]
          }
          if (channelData.channel) {
            channels.push(channelData.channel)
          }
          if (channelData.followers) {
            followers += Number.parseInt(channelData.followers) || 0
          }
          if (channelData.url) {
            url = channelData.url
          }
        })

        // profileImageUrl을 가능한 모든 위치에서 찾기
        let profileImageUrl = ''
        if (signData.profileImageUrl) {
          profileImageUrl = signData.profileImageUrl
        }
        else if (data.profileImageUrl) {
          profileImageUrl = data.profileImageUrl
        }
        else if (data.settlementInfo?.profileImageUrl) {
          profileImageUrl = data.settlementInfo.profileImageUrl
        }

        return {
          id: doc.id,
          name: data.name || '',
          nickname: data.nickname || '',
          email: data.email || '',
          phone: data.phone || '',
          type: data.type || '',
          addressRegion: signData.addressRegion || '',
          addressCity: signData.addressCity || '',
          addressCountry: signData.addressCountry || '',
          addressDetail: signData.addressDetail || '',
          countryCode: signData.countryCode || '',
          profileImageUrl: profileImageUrl || '',
          categories: [...new Set(categories)], // 중복 제거
          channel: [...new Set(channels)], // 중복 제거
          followers,
          url,
          verificationStatus,
          isOwner: true, // 기본값
          ratingAverage: 0, // 기본값
          isActive: true, // 일단 모든 사용자 활성으로 표시
          grade: data.grade || null,
          bio: signData.introduction || data.introduction || '',
          introduction: signData.introduction || data.introduction || '',
          joinDate: data.createdAt || '',
          createdAt: data.createdAt || '',
          updatedAt: data.updatedAt || '',
          keywords: data.keywords || [], // AI 분석 키워드 배열
        } as Creator
      })
    }
    catch {
      error.value = '크리에이터 목록을 불러오는데 실패했습니다.'
    }
    finally {
      loading.value = false
    }
  }

  // 카테고리별 크리에이터 필터링
  const getCreatorsByCategory = (category: string) => {
    return creators.value.filter(creator =>
      creator.categories.some(cat =>
        cat.toLowerCase().includes(category.toLowerCase()),
      ),
    )
  }

  // 지역별 크리에이터 필터링
  const getCreatorsByRegion = (region: string) => {
    return creators.value.filter(creator =>
      creator.addressRegion.toLowerCase().includes(region.toLowerCase()),
    )
  }

  // 채널별 크리에이터 필터링
  const getCreatorsByChannel = (channel: string) => {
    return creators.value.filter(creator =>
      creator.channel.some(ch =>
        ch.toLowerCase().includes(channel.toLowerCase()),
      ),
    )
  }

  // 크리에이터 데이터 AI 분석 함수
  async function analyzeCreatorData(creatorData: Creator) {
    try {
      const creatorText = `
        지역: ${creatorData.addressCountry} ${creatorData.addressRegion} ${creatorData.addressCity}
        주요 카테고리: ${creatorData.categories.join(', ')}
        SNS 채널: ${creatorData.channel.join(', ')}
        총 팔로워 수: ${creatorData.followers || 0}명
        대표 SNS URL: ${creatorData.url}
      `.trim()
      return await requestAIAnalysis('creator', creatorText)
    }
    catch (error) {
      console.error('크리에이터 AI 분석 실패:', error)
      systemStore.showError(getAIAnalysisErrorMessage(error))
      return null
    }
  }

  // 크리에이터 AI 분석 결과를 creator 문서의 keywords 필드에 저장
  async function saveCreatorAIAnalysis(creatorId: string, creatorData: Creator) {
    loading.value = true
    try {
      // AI 분석 수행
      const analysisResult = await analyzeCreatorData(creatorData)

      if (analysisResult) {
        // AI 분석 결과에서 keywords 추출
        const keywords = Array.isArray((analysisResult as any).keywords)
          ? (analysisResult as any).keywords
          : []

        // creator 문서에 keywords 필드 업데이트 (campaign.ts와 동일한 패턴)
        await updateDoc(doc(db, 'creator', creatorId), {
          keywords,
          updatedAt: new Date(),
        })

        return analysisResult
      }

      return null
    }
    catch {
      return null
    }
    finally {
      loading.value = false
    }
  }

  // 키워드로 크리에이터 필터링
  const getCreatorsByKeywords = (keyword: string) => {
    return creators.value.filter(creator =>
      creator.keywords?.some(kw =>
        kw.toLowerCase().includes(keyword.toLowerCase()),
      ),
    )
  }

  // 특정 크리에이터의 키워드 가져오기
  async function getCreatorKeywords(creatorId: string) {
    try {
      const creatorDoc = await getDocs(query(collection(db, 'creator'), where('__name__', '==', creatorId)))
      if (!creatorDoc.empty) {
        const creatorData = creatorDoc.docs[0]?.data()
        return creatorData?.keywords || null
      }
      else {
        return null
      }
    }
    catch {
      return null
    }
  }

  // 키워드가 있는 크리에이터들만 가져오기
  async function getCreatorsWithKeywords() {
    loading.value = true
    try {
      const snapshot = await getDocs(collection(db, 'creator'))
      const creatorsWithKeywords: any[] = []

      snapshot.docs.forEach((doc) => {
        const creatorData = { id: doc.id, ...doc.data() } as Creator
        // keywords 필드가 존재하고 비어있지 않은 크리에이터만 필터링
        if (creatorData?.keywords && creatorData?.keywords !== null) {
          creatorsWithKeywords.push(creatorData)
        }
      })

      return creatorsWithKeywords
    }
    catch {
      return []
    }
    finally {
      loading.value = false
    }
  }

  // 모든 크리에이터에 대해 AI 분석 일괄 수행
  async function analyzeAllCreators() {
    loading.value = true
    try {
      const results = []

      for (const creator of creators.value) {
        const result = await saveCreatorAIAnalysis(creator.id, creator)
        if (result) {
          results.push({ creatorId: creator.id, analysis: result })
        }

        // API 레이트 리미트를 위해 잠시 대기
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      return results
    }
    catch {
      return []
    }
    finally {
      loading.value = false
    }
  }

  // 크리에이터 회원가입
  async function registerCreator(creatorData: {
    nickname: string
    name: string
    email: string
    phone: string
    type: string
    sign: {
      addressCountry: string
      addressCountryName: string
      addressRegion: string
      addressCity: string
      addressDetail: string
      countryCode: string
      countryName: string
      profileImageUrl?: string | null

      snsChannels: Array<{
        channel: string
        isOwner: boolean
        url: string
        accountId: string
        followers: string
        categories: string[]
      }>

      settlementInfo: {
        depositor: string
        bank: string
        account: string
        countryCode: string
        countryName: string
        phone: string
        businessNumber: string
        bizRegion: string
        bizCity: string
        bizDetail: string
        bizFileUrl?: string | null
      }
    }
  }) {
    const systemStore = useSystemStore()
    const userStore = useUserStore()
    loading.value = true

    try {
      if (!userStore.userStoreData?.uid) {
        throw new Error('사용자 인증이 필요합니다.')
      }

      // 이미 업로드된 이미지 URL 사용
      const profileImageUrl = creatorData.sign.profileImageUrl || ''
      const bizFileUrl = creatorData.sign.settlementInfo.bizFileUrl || ''

      // Firestore에 저장할 데이터 구조 - 새로운 구조
      const creatorDocument = {
        // 기본정보 - 최상위 레벨
        uid: userStore.userStoreData.uid,
        nickname: creatorData.nickname,
        name: creatorData.name,
        email: creatorData.email,
        phone: creatorData.phone,
        type: creatorData.type,
        createdAt: serverTimestamp(),

        // 상세정보는 sign 필드 안에 저장
        sign: {
          addressCountry: creatorData.sign.addressCountry,
          addressCountryName: creatorData.sign.addressCountryName,
          addressRegion: creatorData.sign.addressRegion,
          addressCity: creatorData.sign.addressCity,
          addressDetail: creatorData.sign.addressDetail,
          countryCode: creatorData.sign.countryCode,
          countryName: creatorData.sign.countryName,
          profileImageUrl,

          // SNS 채널 배열 (Step 2) - serverTimestamp() 제거
          snsChannels: creatorData.sign.snsChannels.map(channel => ({
            channel: channel.channel,
            isOwner: channel.isOwner,
            url: channel.url,
            accountId: channel.accountId,
            followers: Number.parseInt(channel.followers) || 0,
            categories: channel.categories,
          })),

          // 수익관리 정보 배열 (Step 3) - serverTimestamp() 제거
          settlementInfo: {
            depositor: creatorData.sign.settlementInfo.depositor,
            bank: creatorData.sign.settlementInfo.bank,
            account: creatorData.sign.settlementInfo.account,
            countryCode: creatorData.sign.settlementInfo.countryCode,
            countryName: creatorData.sign.settlementInfo.countryName,
            phone: creatorData.sign.settlementInfo.phone,
            businessNumber: creatorData.sign.settlementInfo.businessNumber,
            bizRegion: creatorData.sign.settlementInfo.bizRegion,
            bizCity: creatorData.sign.settlementInfo.bizCity,
            bizDetail: creatorData.sign.settlementInfo.bizDetail,
            bizFileUrl,
          },
        },

        // 메타데이터
        updatedAt: serverTimestamp(),
      }

      // Firestore creator 컬렉션에 저장 (user 컬렉션은 사용하지 않음)
      await setDoc(doc(db, 'creator', userStore.userStoreData.uid), creatorDocument)

      // 회원가입 완료 후 AI 분석 키워드 추출 수행
      try {
        // 회원가입 데이터를 Creator 인터페이스 형태로 변환
        const creatorForAnalysis: Creator = {
          id: userStore.userStoreData.uid,
          name: creatorData.name,
          nickname: creatorData.nickname,
          email: creatorData.email,
          phone: creatorData.phone,
          type: creatorData.type,
          addressRegion: creatorData.sign.addressRegion,
          addressCity: creatorData.sign.addressCity,
          addressCountry: creatorData.sign.addressCountry,
          addressDetail: creatorData.sign.addressDetail,
          countryCode: creatorData.sign.countryCode,
          profileImageUrl,
          categories: creatorData.sign.snsChannels.flatMap(channel => channel.categories),
          channel: creatorData.sign.snsChannels.map(channel => channel.channel),
          followers: creatorData.sign.snsChannels.reduce((total, channel) =>
            total + (Number.parseInt(channel.followers) || 0), 0),
          url: creatorData.sign.snsChannels[0]?.url || '',
          isOwner: true,
          ratingAverage: 0,
          isActive: true,
          bio: '',
          joinDate: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        // AI 분석 수행 및 결과 저장
        const analysisResult = await saveCreatorAIAnalysis(
          userStore.userStoreData.uid,
          creatorForAnalysis,
        )

        if (analysisResult) {
          systemStore.showSuccess('크리에이터 등록 및 AI 분석이 완료되었습니다.')
        }
        else {
          systemStore.showSuccess('크리에이터 등록이 완료되었습니다.')
        }
      }
      catch {
        // AI 분석 실패해도 회원가입은 성공으로 처리
        systemStore.showSuccess('크리에이터 등록이 완료되었습니다.')
      }

      // router.push는 signUp.vue에서 처리하도록 제거
    }
    catch (error: any) {
      systemStore.showError(error.message || '등록 중 오류가 발생했습니다.')
    }
    finally {
      loading.value = false
    }
  }

  // 크리에이터 검색 조회(FP #19, 2026-09-18). asia-northeast3 배포 함수라
  // functionsAsia 인스턴스로 호출해야 한다(firebaseConfig.ts 주석 참고).
  const searchResults = ref<CreatorSearchResult[]>([])
  const searchLoading = ref(false)

  async function searchCreators(filters: CreatorSearchFilters = {}) {
    searchLoading.value = true
    try {
      const fn = httpsCallable<{ filters: CreatorSearchFilters }, { ok: boolean, creators: CreatorSearchResult[] }>(functionsAsia, 'searchCreators')
      const { data } = await fn({ filters })
      searchResults.value = data.creators || []
      return searchResults.value
    }
    catch (err) {
      console.error('크리에이터 검색 실패:', err)
      systemStore.showError('크리에이터 검색에 실패했습니다.')
      searchResults.value = []
      return []
    }
    finally {
      searchLoading.value = false
    }
  }

  return {
    creators: readonly(creators),
    loading: readonly(loading),
    error: readonly(error),
    getCreators,
    getCreatorsByCategory,
    getCreatorsByRegion,
    getCreatorsByChannel,
    getCreatorsByKeywords,
    analyzeCreatorData,
    saveCreatorAIAnalysis,
    getCreatorKeywords,
    getCreatorsWithKeywords,
    analyzeAllCreators,
    registerCreator,
    searchResults: readonly(searchResults),
    searchLoading: readonly(searchLoading),
    searchCreators,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCreatorStore, import.meta.hot))
}
